import { normalizeWorkflowPeriodicidad } from './workflow-periodicidad';

export type TipoPeriodoData = 'MENSUAL' | 'ANUAL' | 'SEMESTRAL' | 'OTRO';
export type SemestralDataMode = 'offset' | 'calendario';

export interface ProfasePeriodoActividadData {
  tipo?: TipoPeriodoData | string | null;
  mesDeVencimiento?: number | null;
  aplicarAMes?: number | null;
}

export interface ProfasePeriodoActividadEntity {
  id?: number;
  nombre?: string | null;
  tipoPeriodo?: string | null;
  numeroPeriodo?: number | null;
  generarAutomaticamente?: boolean | null;
  data?: ProfasePeriodoActividadData | null;
}

export interface PeriodoActividadFormValues {
  nombre: string;
  tipoPeriodo: string;
  numeroPeriodo: string;
  aplicarAMes: string;
  mesDeVencimiento: string;
  semestralModo: SemestralDataMode;
  generarAutomaticamente: boolean;
}

const MESES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export const MES_CALENDARIO_OPTIONS = MESES.map((label, i) => ({
  value: String(i + 1),
  label: `${i + 1} — ${label}`,
}));

export const APLICAR_A_MES_MENSUAL_OPTIONS = [
  { value: '-1', label: 'Mes anterior al periodo' },
  { value: '0', label: 'Mes vigente (inicio del periodo)' },
  { value: '1', label: 'Mes siguiente al periodo' },
];

export const APLICAR_A_MES_SEMESTRAL_OPTIONS = [
  { value: '0', label: 'Mes 1 del semestre (desde + 0)' },
  { value: '1', label: 'Mes 2 del semestre (desde + 1)' },
  { value: '2', label: 'Mes 3 del semestre (desde + 2)' },
  { value: '3', label: 'Mes 4 del semestre (desde + 3)' },
  { value: '4', label: 'Mes 5 del semestre (desde + 4)' },
  { value: '5', label: 'Mes 6 del semestre (desde + 5)' },
  { value: '6', label: 'Mes 7 del semestre (desde + 6)' },
  { value: '7', label: 'Primer mes posterior al semestre (hasta + 1)' },
];

function normalizeTipo(tipo?: string | null): TipoPeriodoData | '' {
  const norm = normalizeWorkflowPeriodicidad(tipo);
  if (norm === 'MENSUAL' || norm === 'ANUAL' || norm === 'SEMESTRAL' || norm === 'OTRO') {
    return norm;
  }
  return '';
}

export function defaultPeriodoActividadFormValues(
  preferredTipoPeriodo?: string | null,
): PeriodoActividadFormValues {
  const tipo = normalizeTipo(preferredTipoPeriodo) || 'MENSUAL';
  return {
    nombre: '',
    tipoPeriodo: tipo,
    numeroPeriodo: '',
    aplicarAMes: defaultAplicarAMes(tipo),
    mesDeVencimiento: defaultMesDeVencimiento(tipo),
    semestralModo: 'offset',
    generarAutomaticamente: false,
  };
}

function defaultAplicarAMes(tipo: TipoPeriodoData | ''): string {
  if (tipo === 'MENSUAL') return '0';
  if (tipo === 'SEMESTRAL') return '7';
  return '';
}

function defaultMesDeVencimiento(tipo: TipoPeriodoData | ''): string {
  if (tipo === 'ANUAL') return '3';
  return '';
}

export function periodoActividadFormFromEntity(
  entity: ProfasePeriodoActividadEntity,
): PeriodoActividadFormValues {
  const tipoNorm = normalizeTipo(entity.tipoPeriodo);
  const tipoPeriodo = tipoNorm || String(entity.tipoPeriodo ?? '');
  const data = entity.data ?? {};
  const aplicarAMesRaw = data.aplicarAMes;
  const mesDeVencimientoRaw = data.mesDeVencimiento;
  const semestralModo: SemestralDataMode =
    tipoNorm === 'SEMESTRAL' && aplicarAMesRaw == null && mesDeVencimientoRaw != null
      ? 'calendario'
      : 'offset';

  return {
    nombre: entity.nombre?.trim() ?? '',
    tipoPeriodo,
    numeroPeriodo: entity.numeroPeriodo == null ? '' : String(entity.numeroPeriodo),
    aplicarAMes: aplicarAMesRaw == null ? defaultAplicarAMes(tipoNorm) : String(aplicarAMesRaw),
    mesDeVencimiento: mesDeVencimientoRaw == null
      ? defaultMesDeVencimiento(tipoNorm)
      : String(mesDeVencimientoRaw),
    semestralModo,
    generarAutomaticamente: entity.generarAutomaticamente === true,
  };
}

export function resetPeriodoActividadDataFields(
  prev: PeriodoActividadFormValues,
  nextTipoPeriodo: string,
): PeriodoActividadFormValues {
  const tipoNorm = normalizeTipo(nextTipoPeriodo);
  return {
    ...prev,
    tipoPeriodo: nextTipoPeriodo,
    aplicarAMes: defaultAplicarAMes(tipoNorm),
    mesDeVencimiento: defaultMesDeVencimiento(tipoNorm),
    semestralModo: 'offset',
  };
}

function parseNumeroField(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

export function validatePeriodoActividadForm(
  form: PeriodoActividadFormValues,
): Partial<Record<keyof PeriodoActividadFormValues, string>> {
  const errs: Partial<Record<keyof PeriodoActividadFormValues, string>> = {};
  const tipo = normalizeTipo(form.tipoPeriodo);

  if (!form.nombre.trim()) errs.nombre = 'El nombre es requerido';
  if (!tipo) errs.tipoPeriodo = 'El tipo de periodo es requerido';

  if (form.numeroPeriodo.trim() && parseNumeroField(form.numeroPeriodo) == null) {
    errs.numeroPeriodo = 'Ingresa un número válido o deja el campo vacío';
  }

  if (tipo === 'MENSUAL') {
    const n = Number(form.aplicarAMes);
    if (![-1, 0, 1].includes(n)) errs.aplicarAMes = 'Selecciona mes anterior, vigente o siguiente';
  }

  if (tipo === 'ANUAL') {
    const mes = Number(form.mesDeVencimiento);
    if (!Number.isInteger(mes) || mes < 1 || mes > 12) {
      errs.mesDeVencimiento = 'Selecciona un mes de vencimiento (1–12)';
    }
  }

  if (tipo === 'SEMESTRAL') {
    if (form.semestralModo === 'offset') {
      const n = Number(form.aplicarAMes);
      if (!Number.isInteger(n) || n < 0 || n > 7) {
        errs.aplicarAMes = 'Selecciona un offset de semestre (0–7)';
      }
    } else {
      const mes = Number(form.mesDeVencimiento);
      if (!Number.isInteger(mes) || mes < 1 || mes > 12) {
        errs.mesDeVencimiento = 'Selecciona un mes calendario (1–12)';
      }
    }
  }

  return errs;
}

export function buildProfasePeriodoActividadData(
  form: PeriodoActividadFormValues,
): ProfasePeriodoActividadData {
  const tipo = normalizeTipo(form.tipoPeriodo) as TipoPeriodoData;

  if (tipo === 'MENSUAL') {
    return { tipo, aplicarAMes: Number(form.aplicarAMes) };
  }
  if (tipo === 'ANUAL') {
    return { tipo, mesDeVencimiento: Number(form.mesDeVencimiento) };
  }
  if (tipo === 'SEMESTRAL') {
    if (form.semestralModo === 'calendario') {
      return { tipo, aplicarAMes: null, mesDeVencimiento: Number(form.mesDeVencimiento) };
    }
    return { tipo, aplicarAMes: Number(form.aplicarAMes), mesDeVencimiento: null };
  }
  return { tipo: 'OTRO' };
}

export function buildPeriodoActividadPayload(
  form: PeriodoActividadFormValues,
  editId?: number,
): ProfasePeriodoActividadEntity {
  const tipoPeriodo = normalizeTipo(form.tipoPeriodo) || form.tipoPeriodo;
  return {
    ...(editId != null ? { id: editId } : {}),
    nombre: form.nombre.trim(),
    tipoPeriodo,
    numeroPeriodo: parseNumeroField(form.numeroPeriodo),
    generarAutomaticamente: form.generarAutomaticamente === true,
    data: buildProfasePeriodoActividadData(form),
  };
}

export function formatPeriodoActividadDataLabel(
  data?: ProfasePeriodoActividadData | null,
  tipoPeriodo?: string | null,
): string {
  const tipo = normalizeTipo(data?.tipo ?? tipoPeriodo);
  if (!tipo || tipo === 'OTRO') return 'Sin referencia específica';

  if (tipo === 'MENSUAL') {
    const opt = APLICAR_A_MES_MENSUAL_OPTIONS.find(o => o.value === String(data?.aplicarAMes ?? 0));
    return opt?.label ?? `Aplicar a mes ${data?.aplicarAMes ?? 0}`;
  }

  if (tipo === 'ANUAL') {
    const mes = data?.mesDeVencimiento ?? 3;
    const label = MESES[mes - 1];
    return label ? `Vencimiento ${label} (año siguiente)` : `Mes ${mes}`;
  }

  if (tipo === 'SEMESTRAL') {
    if (data?.aplicarAMes != null) {
      const opt = APLICAR_A_MES_SEMESTRAL_OPTIONS.find(o => o.value === String(data.aplicarAMes));
      return opt?.label ?? `Offset semestre ${data.aplicarAMes}`;
    }
    const mes = data?.mesDeVencimiento;
    if (mes != null) {
      const label = MESES[mes - 1];
      return label ? `Mes calendario ${label}` : `Mes calendario ${mes}`;
    }
    return 'Semestral';
  }

  return '—';
}

export function periodoActividadApiErrorMessage(error: unknown, fallback: string): string {
  const err = error as {
    response?: { data?: { detail?: string; message?: string; title?: string; errorKey?: string } };
    message?: string;
  };
  const data = err?.response?.data;
  const key = data?.errorKey;
  const map: Record<string, string> = {
    tipoperiodorequired: 'El tipo de periodo es obligatorio.',
    datatipomismatch: 'La configuración data no coincide con el tipo de periodo.',
    aplicaramesinvalid: 'El valor de aplicar a mes no es válido para este tipo.',
    mesdevencimientoinvalid: 'El mes de vencimiento debe estar entre 1 y 12.',
    semestraldatainvalid: 'La configuración semestral no es válida.',
    mensualdatainvalid: 'La configuración mensual no es válida.',
    anualdatainvalid: 'La configuración anual no es válida.',
  };
  if (key && map[key]) return map[key];
  return data?.detail ?? data?.message ?? data?.title ?? err?.message ?? fallback;
}
